import { getOpeningKindProperty } from '@pointorama/potree-shared';
import { OpeningKind } from 'shared-types';
import { isDoorWindowAnnotation } from '../utils/PointoramaAnnotation';
import type { Viewer } from './viewer';
import type { RenderView } from './RenderView';

/**
 * Categories an annotation can be filtered by. Mirrors how annotations are
 * partitioned across the viewer's Scene arrays — there is no single
 * `.type` field on every annotation class, so the category is supplied at
 * iteration time (we know which Scene array we're looping).
 *
 * `OPENING` is a sub-category of `AREA` — an OpeningAnnotation is also an
 * AreaAnnotation, but for blacklist purposes we treat it as its own type
 * so callers can hide doors/windows without dropping every area annotation.
 */
export type AnnotationCategory = 'POINT' | 'DISTANCE' | 'AREA' | 'OPENING' | 'OPENING_SKETCH' | 'SKETCH_OBJECT' | 'ROOM' | 'WALL_SKETCH' | 'PLANE' | 'BOX' | 'POINTCLUSTER' | 'SURFACE' | 'OUTLINE';

/**
 * Per-RenderView annotation filter, expressed as a set of **facets** — which
 * category, which identity (own id / group / floor), which opening-kind.
 * Every facet present below is a constraint; how they combine depends on
 * `mode`:
 *
 *  - `blacklist` (default): an annotation is hidden if it matches ANY facet.
 *  - `whitelist`: an annotation is shown only if it satisfies EVERY facet
 *    that is set, and hidden otherwise. An absent or empty facet imposes no
 *    constraint.
 *
 * Within a single facet the members are OR-ed (`types: {DOOR, WINDOW}` means
 * door OR window). The two modes are De Morgan duals: whitelist must pass
 * every facet, blacklist is blocked by any facet. The `.visible` flip lasts
 * one render, then is restored.
 */
export interface AnnotationBlacklist {
  /** `blacklist` (default) hides matches; `whitelist` hides everything except matches. */
  mode?: 'blacklist' | 'whitelist';
  /** Category facet — e.g. all PLANEs, all OPENINGs. */
  types?: ReadonlySet<AnnotationCategory>;
  /**
   * Identity facet — matches when this set contains an annotation's own
   * `identifier` OR its `groupIdentifier`, so one entry covers a single
   * annotation or a whole group / floor.
   */
  identifiers?: ReadonlySet<string>;
  /** Opening-kind facet (DOOR / WINDOW). Non-Opening annotations never satisfy it. */
  openingKinds?: ReadonlySet<OpeningKind>;
}

/** Subset of THREE.Object3D fields we need from every annotation. */
interface AnnotationLike {
  visible: boolean;
  identifier?: string;
  groupIdentifier?: string | null;
}

const NOOP = (): void => {
  // No annotations were hidden — nothing to restore.
};

/**
 * Blacklist predicate — true iff the annotation matches ANY facet. The caller
 * supplies the category since not every annotation class carries a `.type`.
 */
function matchesAnyFacet(obj: AnnotationLike, category: AnnotationCategory, bl: AnnotationBlacklist): boolean {
  if (bl.identifiers) {
    if (obj.identifier && bl.identifiers.has(obj.identifier)) return true;
    // A group / floor id in the set covers every annotation tagged with it.
    if (obj.groupIdentifier && bl.identifiers.has(obj.groupIdentifier)) return true;
  }
  if (bl.types?.has(category)) return true;
  if (category === 'OPENING' && bl.openingKinds && bl.openingKinds.size > 0 && obj.identifier) {
    try {
      if (bl.openingKinds.has(getOpeningKindProperty(obj.identifier).value)) return true;
    } catch {
      // Property service not initialised yet — fall through.
    }
  }
  return false;
}

/**
 * Whitelist predicate — true iff the annotation satisfies EVERY facet that is
 * set. An absent or empty facet imposes no constraint.
 */
function passesAllFacets(obj: AnnotationLike, category: AnnotationCategory, bl: AnnotationBlacklist): boolean {
  if (bl.identifiers && bl.identifiers.size > 0) {
    const inScope =
      (!!obj.identifier && bl.identifiers.has(obj.identifier)) ||
      (!!obj.groupIdentifier && bl.identifiers.has(obj.groupIdentifier));
    if (!inScope) return false;
  }
  if (bl.types && bl.types.size > 0 && !bl.types.has(category)) return false;
  if (bl.openingKinds && bl.openingKinds.size > 0) {
    if (category !== 'OPENING' || !obj.identifier) return false;
    try {
      if (!bl.openingKinds.has(getOpeningKindProperty(obj.identifier).value)) return false;
    } catch {
      // Property service not initialised yet — can't confirm the kind.
      return false;
    }
  }
  return true;
}

/**
 * Resolve the filter for one annotation: blacklist hides anything matching a
 * facet; whitelist hides anything that fails a facet.
 */
function shouldHide(obj: AnnotationLike, category: AnnotationCategory, bl: AnnotationBlacklist): boolean {
  if (bl.mode === 'whitelist') return !passesAllFacets(obj, category, bl);
  return matchesAnyFacet(obj, category, bl);
}

/**
 * Picking counterpart to the render-time blacklist: returns true iff the given
 * annotation would be HIDDEN by the active view's blacklist, so raycast/selection
 * paths can skip it. The render blacklist only flips `.visible` during a frame
 * (restored immediately after), so picking — which runs between frames — would
 * otherwise still hit hidden objects. Callers pass the category since not every
 * annotation class carries a usable `.type`.
 *
 * Uses the main render view's blacklist (the one the user interacts with). When
 * no blacklist is set, nothing is excluded.
 */
/** Minimal viewer shape needed to resolve the active view's blacklist. */
interface BlacklistViewerLike {
  renderViews: Array<{ viewState?: { getAnnotationBlacklist(): AnnotationBlacklist | null } | null }>;
  /** Subset of FloorplanRepresentationManager — hides a floor's rooms/outlines while that floor shows the skinned view. */
  floorplanRepresentation?: { shouldHideOriginalAnnotations(floorGroupId: string | null | undefined): boolean };
  /** Subset of PersonaManager — gates sketch picking on the floorplan persona's mode. */
  personaManager?: { getActivePersona(): { name: string; getCurrentMode(): { id: string } | undefined } | undefined };
  /** Subset of Scene — resolves legacy-outline tombstones (floor's sketch
   *  carries merged outline edges). `wallSketches` is optional so narrower
   *  viewer facades (e.g. the InputHandler's) stay assignable; without it the
   *  tombstone check simply doesn't block. */
  scene?: { wallSketches?: Array<{ groupIdentifier: string | null; getOutlineEdges(): unknown[] }> };
}

export function isAnnotationPickBlocked(viewer: BlacklistViewerLike, obj: AnnotationLike, category: AnnotationCategory): boolean {
  // Legacy OUTLINE tombstone: superseded by its floor's merged outline (the
  // wall sketch carries `role:'OUTLINE'` edges) — never pickable. Mirrors the
  // render-time force in applyAnnotationBlacklist.
  if (category === 'OUTLINE' && obj.groupIdentifier && viewer.scene?.wallSketches?.some((s) => s.groupIdentifier === obj.groupIdentifier && s.getOutlineEdges().length > 0)) {
    return true;
  }
  // Rooms/outlines hidden by their floor's skinned wall representation must
  // not be pickable either — mirrors the render-time force in
  // applyAnnotationBlacklist.
  if ((category === 'ROOM' || category === 'OUTLINE') && viewer.floorplanRepresentation?.shouldHideOriginalAnnotations(obj.groupIdentifier)) {
    return true;
  }
  // Sketch annotations are editable only in the floorplan persona's Design
  // tab — in its other modes they stay visible but must not be selectable.
  // The layout persona (single edit mode, name 'layout') is unaffected.
  if (category === 'OPENING_SKETCH' || category === 'SKETCH_OBJECT' || category === 'WALL_SKETCH') {
    const persona = viewer.personaManager?.getActivePersona();
    if (persona?.name === 'floorplan' && persona.getCurrentMode()?.id !== 'design') return true;
  }
  const view = viewer.renderViews[0];
  const bl = view?.viewState?.getAnnotationBlacklist();
  if (!bl) return false;
  return shouldHide(obj, category, bl);
}

/**
 * Apply this view's annotation filter by flipping `.visible = false` on the
 * annotations it hides (see `shouldHide` — facet matches in blacklist mode,
 * facet failures in whitelist mode). Returns a restore closure that undoes
 * every flip — call it inside a `finally` so a render throw still un-hides
 * every touched annotation.
 *
 * Frame-local mutation pattern: the annotation Object3Ds live in scenes
 * shared across all RenderViews, so a permanent `.visible = false` would
 * leak into other views. By restoring at end-of-frame, every other view
 * sees the scene untouched.
 *
 * Annotations that were already `.visible = false` (e.g. hidden by cadObject
 * sync) are skipped entirely — we don't track them and won't re-show them.
 */
export function applyAnnotationBlacklist(viewer: Viewer, view: RenderView): () => void {
  const bl = view.viewState?.getAnnotationBlacklist();

  // Sketch annotations (WALL_SKETCH / OPENING_SKETCH / SKETCH_OBJECT) are
  // persona-gated, enforced here at the single render choke point so
  // switching personas always hides them (personas that don't set a
  // blacklist, or set a floor whitelist that would otherwise pass sketches,
  // can't leak them). Both the layout persona AND the floorplan persona
  // (wall + design modes create/edit all three) show them; every other
  // persona hides them.
  const persona = viewer.personaManager?.getActivePersona();
  const activePersona = persona?.name;
  const hideSketches = activePersona !== 'layout' && activePersona !== 'floorplan';

  // Sketch OBJECTS (furniture/fixture symbols) are a Design-tab concern: in
  // the floorplan persona's other tabs they hide, while wall sketches and
  // openings stay (the Wall tab edits those).
  const hideSketchObjects = activePersona === 'floorplan' && persona?.getCurrentMode()?.id !== 'design';

  // A floor showing the SKINNED wall representation hides its room + outline
  // lines (redundant with the wall skin) — per floor, resolved from each
  // annotation's group — except while a wall-thickness drag temporarily
  // "peeks" at them (see FloorplanRepresentationManager.beginOriginalPeek).
  // The source is global; this precondition only rules out contexts where no
  // floor's lines can be hidden (other personas, room/floor editing tabs).
  const mayHideOriginal = viewer.floorplanRepresentation?.mayHideOriginalAnnotations() ?? false;

  // Room tag labels (RoomLabelsLayer sprites) aren't scene annotations, so
  // the category loops below never see them — the layer resolves which of its
  // sprites this view's filter (or a hidden room measure) locks out.
  const floorLockedTags = viewer.roomLabelsLayer?.collectFloorLockedSprites(bl ?? null) ?? [];

  // Legacy OUTLINE tombstones: floors whose wall sketch carries merged
  // `role:'OUTLINE'` edges supersede their OUTLINE annotations — those stay
  // hidden everywhere (undoing the conversion drops the merged edges, which
  // un-hides them again automatically).
  const mergedOutlineFloors = new Set<string>();
  for (const s of viewer.scene.wallSketches) {
    if (s.groupIdentifier && s.getOutlineEdges().length > 0) mergedOutlineFloors.add(s.groupIdentifier);
  }

  if (!bl && !hideSketches && !mayHideOriginal && !hideSketchObjects && floorLockedTags.length === 0 && mergedOutlineFloors.size === 0) return NOOP;

  const restores: Array<() => void> = [];
  // Frame-local hide of any Object3D-with-`.visible`, restored at end of frame.
  const hideObject = (obj: { visible: boolean }) => {
    if (!obj.visible) return;
    obj.visible = false;
    restores.push(() => {
      obj.visible = true;
    });
  };
  const hide = (obj: AnnotationLike, category: AnnotationCategory) => {
    const sketchForced = (hideSketches || (hideSketchObjects && category === 'SKETCH_OBJECT')) && (category === 'WALL_SKETCH' || category === 'OPENING_SKETCH' || category === 'SKETCH_OBJECT');
    const originalForced = mayHideOriginal && (category === 'ROOM' || category === 'OUTLINE') && (viewer.floorplanRepresentation?.shouldHideOriginalAnnotations(obj.groupIdentifier) ?? false);
    if (!sketchForced && !originalForced && (!bl || !shouldHide(obj, category, bl))) return;
    hideObject(obj);
  };

  for (const m of viewer.scene.measurements) {
    const category: AnnotationCategory = isDoorWindowAnnotation(m) ? 'OPENING' : (m.type as AnnotationCategory);
    hide(m, category);
  }
  for (const p of viewer.scene.planes) hide(p, 'PLANE');
  for (const v of viewer.scene.volumes) {
    // viewer.scene.volumes mixes BoxVolume and SphereVolume; only BoxVolume is
    // an annotation. SphereVolumes are clip helpers — skip via identifier-only
    // filtering, which they won't match unless explicitly listed.
    hide(v, 'BOX');
  }
  for (const r of viewer.scene.rooms) hide(r, 'ROOM');
  for (const s of viewer.scene.wallSketches) {
    hide(s, 'WALL_SKETCH');
    // The wall skin lives in a group outside the sketch object's child tree,
    // so `.visible = false` on the sketch doesn't hide it. An explicit
    // blacklist match (e.g. the floor whitelist) hides the skin in BOTH modes
    // — a non-selected floor's 3D walls must not leak into the selected
    // floor's view. Outside the layout persona only the 2D skin is forced
    // hidden: the 3D walls are the floor's own representation and stay shown.
    if (bl && shouldHide(s, 'WALL_SKETCH', bl)) {
      const skin = s.skinningVisualsGroup;
      if (skin) hideObject(skin);
    } else if (hideSketches) {
      const skin2D = s.skinning2DGroup;
      if (skin2D) hideObject(skin2D);
    }
  }
  for (const c of viewer.scene.pointClusters) hide(c, 'POINTCLUSTER');
  for (const surf of viewer.scene.surfaces) hide(surf, 'SURFACE');
  for (const o of viewer.scene.outlines) {
    if (o.groupIdentifier && mergedOutlineFloors.has(o.groupIdentifier)) {
      hideObject(o);
      continue;
    }
    hide(o, 'OUTLINE');
  }
  for (const os of viewer.scene.openingSketches) hide(os, 'OPENING_SKETCH');
  for (const so of viewer.scene.sketchObjects) hide(so, 'SKETCH_OBJECT');
  for (const tag of floorLockedTags) hideObject(tag);

  if (restores.length === 0) return NOOP;
  return () => {
    for (const r of restores) r();
  };
}
